import createDOMPurify from 'dompurify';

/**
 * Mirrors the allowlist the backend applies to progress domainMeta when it receives it
 * (web-be ProgressHtmlSanitizer): that one is the trust boundary, this is the second layer
 * before v-html.
 *
 * - No img/iframe/svg: a remote src is a tracking pixel.
 * - No style, and no class but the el-tag ones on a span: publisher styling would leak into the
 *   app's own CSS. A tag is the one Element UI look a static snippet can borrow. A tbody may be
 *   a progress-group.
 * - No h1/h2: the page owns its outline.
 * - https: and mailto: links only, so javascript:, data: and protocol-relative URLs are dropped.
 */
const CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption',
    'span', 'div', 'code', 'small', 'hr', 'a',
  ],
  // DOMPurify allows attributes on every tag: harmless here, colspan on a link does nothing.
  // class is narrowed down to ALLOWED_CLASSES by the hook below.
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'colspan', 'rowspan', 'class'],
  ALLOWED_URI_REGEXP: /^(?:https|mailto):/i,
  // DOMPurify checks every other attribute's value against the regexp above too: these hold
  // no URL, so they are exempted or "_blank" and "2" would be dropped.
  ADD_URI_SAFE_ATTR: ['target', 'rel', 'colspan', 'rowspan'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

// The classes each element may keep. A tbody may be a progress-group (SMP-1704), which
// ProgressDomainMeta renders as an accordion.
const ALLOWED_CLASSES = {
  SPAN: [
    'el-tag',
    'el-tag--small',
    'el-tag--success',
    'el-tag--warning',
    'el-tag--danger',
    'el-tag--info',
  ],
  TBODY: ['progress-group'],
};

// An instance of its own, so the hook below does not apply to other DOMPurify users.
const purify = createDOMPurify(window);

purify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName !== 'class') return;

  const allowed = ALLOWED_CLASSES[node.tagName] || [];
  const classes = data.attrValue.split(/\s+/).filter(name => allowed.includes(name));
  // Rewriting data is how a DOMPurify hook changes or drops the attribute.
  /* eslint-disable no-param-reassign */
  data.attrValue = classes.join(' ');
  data.keepAttr = classes.length > 0;
  /* eslint-enable no-param-reassign */
});

// Overwrites any publisher rel, so target="_blank" cannot reach window.opener.
purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') node.setAttribute('rel', 'noopener noreferrer');
});

export default {
  /** Empty when nothing survives, so the caller can tell there is nothing to show. */
  sanitize(html) {
    return html ? purify.sanitize(html, CONFIG).trim() : '';
  },
};
